/* eslint-disable @reatom/unit-naming-rule */
import { atom, filter } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { pairRegistry } from '@root/entities/pair/atoms'
import { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models'
import { reatomDataTable } from '@root/shared/ui-kit'

import { distributeTableColumns } from '../ui/distribute-table/distribute-table-columns'

import type { DistributeRange, PairPerformance } from '../model'
import type { DelugeId } from '@root/entities/user-short'
import type { TableData } from '@root/shared/ui-kit'

import { fetchDistributeRequestAction } from './'

export type DistributeRowVariant = 'default' | 'stacked'
export type DistributeTableColumn = 'currency' | 'realized'

export const distributeRangeAtom = atom<DistributeRange | undefined>(
  undefined,
  'distributeRangeAtom',
)

export const reatomDistributeTable = (
  delugeId: DelugeId,
  rowVariant: DistributeRowVariant = 'default',
) => {
  const periodAtom = atom<UserTradingAnalyticsPeriod>(
    UserTradingAnalyticsPeriod.ALL,
  )
  const paramsAtom = atom((ctx) => {
    const range = ctx.spy(distributeRangeAtom) ?? [-Infinity, Infinity]
    const period = ctx.spy(periodAtom)

    return { period, range, delugeId }
  }, 'paramsAtom').pipe(filter())

  const tableModel = reatomDataTable({
    columns: distributeTableColumns(rowVariant),
    name: 'distributeTable',
    paramsAtom,
    loadItems: async (
      ctx,
      {
        params: { period, range, delugeId },
      }: {
        params: {
          period: UserTradingAnalyticsPeriod
          range: DistributeRange
          delugeId: DelugeId
        }
      },
    ) => {
      const result = await fetchDistributeRequestAction(ctx, {
        period,
        range,
        delugeId,
      })

      return pipe(
        result,
        Result.map(({ tokens }): TableData<PairPerformance> => {
          const data = tokens.flatMap((token) => {
            if (token.pairMeta === null) {
              return []
            }

            // TODO: Ivan - drop filter by range when the backend is ready
            if (!matchesDistributeRange(token.pnlPercent, range)) {
              return []
            }

            return [
              {
                pair: pairRegistry.resolveFromDto(ctx, token.pairMeta),
                performanceUsd: Number(token.realizedPnlUsd),
                performancePercent: token.realizedPnlPercent,
              },
            ]
          })

          return {
            data,
            total: data.length,
          }
        }),
      )
    },
    limit: 20,
    getId: (x) => x.pair.address,
  })

  return { tableModel, distributeRangeAtom, periodAtom }
}

export type DistributeTableModel = ReturnType<typeof reatomDistributeTable>

const matchesDistributeRange = (
  pnlPercent: number,
  [min, max]: DistributeRange,
): boolean => {
  if (min === -Infinity && max === Infinity) {
    return true
  }

  return pnlPercent >= min && pnlPercent <= max
}
